/**
 * AuthForm component - Login and Registration forms.
 * Material Design themed with elegant styling.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

type ViewMode = 'login' | 'register';

const AuthForm: React.FC = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [viewMode, setViewMode] = useState<ViewMode>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!loginEmail || !loginPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!loginEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      const loggedInUser = await login(loginEmail, loginPassword);

      // T082: Conditional redirect based on onboarding status and current_page
      const onboarding = loggedInUser.onboarding;
      if (onboarding && !onboarding.completed && !onboarding.dismissed) {
        // Feature 016: Resume onboarding from current page
        const onboardingPages = [
          '/onboarding/personal-details', // current_page = 0 or 1
          '/onboarding/goals',             // current_page = 2
          '/onboarding/body-state',        // current_page = 3
          '/onboarding/struggles'          // current_page = 4
        ];

        const currentPage = onboarding.current_page || 0;
        const targetPage = currentPage === 0 ? 0 : Math.min(currentPage - 1, 3);
        navigate(onboardingPages[targetPage]);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Feature 016: Simplified registration - email and password only
    // Basic validation
    if (!registerEmail || !registerPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!registerEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (registerPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (registerPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      // Feature 016: Register with email and password only (no profile)
      await register(registerEmail, registerPassword);

      // Feature 016: Always redirect to onboarding for new users
      navigate('/onboarding/personal-details');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 px-4 py-12">
      <div className="max-w-lg w-full animate-fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-serif font-bold text-primary-800 mb-3">
            BOSI
          </h1>
          <p className="text-base text-primary-600 font-normal mb-1" role="doc-subtitle">
            Your Body Signal Intelligence
          </p>
          <p className="text-lg text-primary-700 font-light">
            Listen to your body. Live stronger.
          </p>
        </div>

        <div className="card shadow-elegant">
          {/* View Toggle */}
          <div className="flex mb-8 bg-primary-50 rounded-full p-1.5">
            <button
              type="button"
              onClick={() => {
                setViewMode('login');
                setError(null);
              }}
              className={`flex-1 py-3 px-6 rounded-full font-medium transition-all duration-300 ${
                viewMode === 'login'
                  ? 'bg-white text-primary-700 shadow-md'
                  : 'text-primary-600 hover:text-primary-800'
              }`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('register');
                setError(null);
              }}
              className={`flex-1 py-3 px-6 rounded-full font-medium transition-all duration-300 ${
                viewMode === 'register'
                  ? 'bg-white text-primary-700 shadow-md'
                  : 'text-primary-600 hover:text-primary-800'
              }`}
            >
              Register
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg animate-slide-down">
              <p className="text-sm text-red-800 font-medium">{error}</p>
            </div>
          )}

          {/* Login Form */}
          {viewMode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-neutral-700 mb-2">
                  Email Address
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="input input-large"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-neutral-700 mb-2">
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="input input-large"
                  placeholder="Enter your password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full text-lg py-4 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {/* Register Form */}
          {viewMode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <label htmlFor="register-email" className="block text-sm font-medium text-neutral-700 mb-2">
                  Email Address
                </label>
                <input
                  id="register-email"
                  type="email"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  className="input input-large"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="register-password" className="block text-sm font-medium text-neutral-700 mb-2">
                  Password (min 8 characters)
                </label>
                <input
                  id="register-password"
                  type="password"
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  className="input input-large"
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-neutral-700 mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input input-large"
                  placeholder="Re-enter your password"
                  required
                />
              </div>

              {/* Feature 016: Simplified registration - removed all profile fields */}

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full text-lg py-4 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Footer Note */}
          <p className="mt-8 text-xs text-center text-neutral-500">
            By continuing, you agree to our{' '}
            <a href="#" className="text-primary-600 hover:text-primary-700 underline">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="#" className="text-primary-600 hover:text-primary-700 underline">
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Feature 016: Removed target warning modal and old form fields - orphaned HTML deleted */}
      </div>
    </div>
  );
};

export default AuthForm;
